import React, { useEffect, useState } from 'react';
import { FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { FaPencil } from "react-icons/fa6";
import { toast, Toaster } from 'react-hot-toast';
import ApiService from '../models/ApiService';

const VocabularyCatMaster = () => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({ id: null, vocabulary_category: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(null); // Track individual toggle loading states
  const [error, setError] = useState(null);

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.vocabulary_category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchVocabularyCategories();
  }, []);

  const fetchVocabularyCategories = async () => {
    setIsLoading(true);
    try {
      const res = await ApiService.GetVocabularyCategory();
      console.log('API Response:', res);

      if (res.data.responseContent && Array.isArray(res.data.responseContent)) {
        setCategories(res.data.responseContent);
      } else {
        setCategories([]);
        throw new Error('Invalid data format received from server');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch categories');
      toast.error('Failed to fetch categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      console.log('Form data being submitted:', formData);

      let response;
      if (formData.id) {
        // Update
        response = await ApiService.UpdateVocabularyCategory(formData.id, { vocabulary_category: formData.vocabulary_category });
        console.log('Update response:', response);
        if (response.status !== 200) {
          throw new Error(response.data?.message || 'Update failed');
        }
        toast.success('Category updated successfully!');
      } else {
        // Add
        response = await ApiService.AddVocabularyCategory({ vocabulary_category: formData.vocabulary_category });
        console.log('Add response:', response);
        if (response.status !== 201) {
          throw new Error(response.data?.message || 'Add failed');
        }
        toast.success('Category added successfully!');
      }

      await fetchVocabularyCategories();
      setIsModalOpen(false);
      setFormData({ id: null, vocabulary_category: '' });
    } catch (err) {
      console.error('Detailed error:', {
        message: err.message,
        response: err.response,
        stack: err.stack
      });
      const errorMsg = err.response?.data?.message || err.message || 'Operation failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (category) => {
    setFormData({
      id: category.id,
      vocabulary_category: category.vocabulary_category
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      setIsLoading(true);
      try {
        const response = await ApiService.DeleteVocabularyCategory(id);
        console.log('Delete response:', response);
        if (response.status === 200) {
          toast.success('Category deleted successfully!');
          await fetchVocabularyCategories();
        } else {
          throw new Error(response.data.message || 'Delete failed');
        }
      } catch (err) {
        console.error('Delete error:', err);
        const errorMsg = err.response?.data?.message || err.message || 'Failed to delete category';
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTogglePublish = async (id, currentStatus) => {
    if (toggleLoading === id) return; // Prevent multiple clicks on same toggle

    setToggleLoading(id); // Set loading for this specific toggle
    try {
      console.log('Toggling category:', id, 'from:', currentStatus, 'to:', !currentStatus);

      const newStatus = { is_published: !currentStatus };

      // Optimistically update UI first
      setCategories(prevCategories =>
        prevCategories.map(category =>
          category.id === id
            ? { ...category, is_published: !currentStatus }
            : category
        )
      );

      const response = await ApiService.VocabularyCategoryToggle(id, newStatus);
      console.log('Toggle API response:', response);

      if (response.status !== 200) {
        // Revert if API call fails
        setCategories(prevCategories =>
          prevCategories.map(category =>
            category.id === id
              ? { ...category, is_published: currentStatus } // Revert to original state
              : category
          )
        );
        throw new Error(response.data?.message || 'Toggle failed');
      }

      toast.success(`Category ${!currentStatus ? 'published' : 'unpublished'} successfully!`);
    } catch (err) {
      console.error('Toggle error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to update status');
    } finally {
      setToggleLoading(null); // Clear loading state
    }
  };

  return (
    <div className="mx-auto" id='primary-fonts'>
      <Toaster />
      <h1 className="text-2xl font-bold mb-8 text-[--secondary-color]">Vocabulary Categories</h1>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Search and Add Button */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-64">
          <input
            type="text"
            placeholder="Search categories..."
            className="w-full pl-10 pr-4 py-2 border border-[--border-color-o] focus:outline-none focus:ring-1 focus:ring-[--border-color-o] rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full md:w-auto bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition duration-200"
          disabled={isLoading}
        >
          <><FaPlus /> Add Category</>
        </button>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[--primary-color]"></div>
        </div>
      ) : (
        /* Table */
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[--header-color-g]">
              <tr>
                <th className="px-6 py-3 text-left uppercase text-white">SR.NO</th>
                <th className="px-6 py-3 text-left uppercase text-white">Category Name</th>
                <th className="px-6 py-3 text-left uppercase text-white">Status</th>
                <th className="px-6 py-3 text-left uppercase text-white">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category, index) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-black">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-black">{category.vocabulary_category}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {/* Toggle Switch Button */}
                      <button
                        onClick={() => handleTogglePublish(category.id, category.is_published)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ease-in-out focus:outline-none ${category.is_published
                            ? 'bg-green-500'
                            : 'bg-gray-300'
                          } ${toggleLoading === category.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
                        disabled={toggleLoading === category.id}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300 ease-in-out ${category.is_published ? 'translate-x-6' : 'translate-x-1'
                            } ${toggleLoading === category.id ? 'opacity-70' : ''}`}
                        />
                        {toggleLoading === category.id && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          </div>
                        )}
                        <span className="sr-only">
                          {category.is_published ? 'Published' : 'Unpublished'}
                        </span>
                      </button>
                      <span className="ml-2 text-sm text-gray-600">
                        {category.is_published ? 'Published' : 'Unpublished'}
                        {toggleLoading === category.id && ' (Updating...)'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className="p-2 text-[--primary-btn-color] rounded-full transition-colors hover:bg-blue-50"
                          disabled={isLoading || toggleLoading}
                          title="Edit"
                        >
                          <FaPencil />
                        </button>
                        <button
                          onClick={() => handleDelete(category.id)}
                          className="p-2 text-red-600 rounded-full transition-colors hover:bg-red-50"
                          disabled={isLoading || toggleLoading}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                    No categories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for Add/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {formData.id ? 'Edit Category' : 'Add New Category'}
              </h2>
              {error && (
                <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2" htmlFor="vocabulary_category">
                    Category Name
                  </label>
                  <input
                    type="text"
                    id="vocabulary_category"
                    name="vocabulary_category"
                    value={formData.vocabulary_category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setFormData({ id: null, vocabulary_category: '' });
                      setError(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition duration-200"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition duration-200"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : (formData.id ? 'Update' : 'Save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyCatMaster;